/**
 * ContextSelector.tsx - Navigation context selector using Selectr
 * 
 * CHANGE HISTORY:
 * - 251122_1508 danielbeer: Initial creation with Selectr integration
 * 
 * Allows viewing/editing navigation contexts:
 * - Fixed contexts (from Organizr)
 * - User contexts (from Templatr)
 * - Dynamic contexts (from FileMaker JSON)
 * 
 * Uses Selectr for display with Filtr for advanced filtering
 */

import React, { useState, useEffect } from 'react';
import { Selectr } from '@opcentral/selectr';
import type { SelectrItem } from '@opcentral/selectr';

export interface NavigationContext {
  id: string;
  name: string;
  description?: string;
  type: 'fixed' | 'user' | 'dynamic';
  items: NavigationItem[];
  filters?: FilterConfig;
  layout?: LayoutConfig;
  icon?: string;
  metadata?: Record<string, any>;
}

export interface NavigationItem {
  id: string;
  label: string;
  icon?: string;
  description?: string;
  category?: string;
  tags?: string[];
  action?: () => void;
  metadata?: Record<string, any>;
}

export interface FilterConfig {
  fuzzySearch?: boolean;
  searchFields?: string[];
  highlightMatches?: boolean;
  caseSensitive?: boolean;
}

export interface LayoutConfig {
  grouped?: boolean;
  sortBy?: 'name' | 'recent' | 'category';
  showIcons?: boolean;
  showDescriptions?: boolean;
}

export interface ContextSelectorProps {
  // Data sources
  fixedContexts?: NavigationContext[];      // From Organizr
  userContexts?: NavigationContext[];       // From Templatr
  dynamicContext?: NavigationContext;       // From FileMaker JSON
  
  // Current state
  currentContextId?: string;
  
  // Callbacks
  onContextChange?: (context: NavigationContext) => void;
  onContextEdit?: (context: NavigationContext) => void;
  onContextCreate?: () => void;
  onContextDelete?: (contextId: string) => void;
  
  // Display options
  mode?: 'view' | 'edit';
  allowEdit?: boolean;
  allowCreate?: boolean;
  allowDelete?: boolean;
}

/**
 * ContextSelector Component
 * 
 * Displays navigation contexts using Selectr with Filtr integration
 * Supports viewing and editing contexts from multiple sources
 */
export const ContextSelector: React.FC<ContextSelectorProps> = ({
  fixedContexts = [],
  userContexts = [],
  dynamicContext,
  currentContextId,
  onContextChange,
  onContextEdit,
  onContextCreate,
  onContextDelete,
  mode = 'view',
  allowEdit = true,
  allowCreate = true,
  allowDelete = true
}) => {
  const [contexts, setContexts] = useState<NavigationContext[]>([]);
  const [selectedContext, setSelectedContext] = useState<NavigationContext | null>(null);

  // Combine all contexts
  useEffect(() => {
    const allContexts = [
      ...fixedContexts,
      ...userContexts,
      ...(dynamicContext ? [dynamicContext] : [])
    ];
    setContexts(allContexts);

    // Set selected context
    if (currentContextId) {
      const current = allContexts.find(c => c.id === currentContextId);
      if (current) {
        setSelectedContext(current);
      }
    }
  }, [fixedContexts, userContexts, dynamicContext, currentContextId]);

  // Convert contexts to Selectr items
  const contextItems: SelectrItem[] = contexts.map(context => ({
    id: context.id,
    label: context.name,
    value: context,
    icon: context.icon || getContextIcon(context.type),
    description: context.description,
    group: getContextGroup(context.type),
    metadata: {
      type: context.type,
      itemCount: context.items.length,
      ...context.metadata
    }
  }));

  // Handle context selection
  const handleContextSelect = (item: SelectrItem | SelectrItem[]) => {
    if (Array.isArray(item)) return; // Single select only
    
    const context = item.value as NavigationContext;
    setSelectedContext(context);
    
    if (onContextChange) {
      onContextChange(context);
    }
  };

  // Get icon for context type
  const getContextIcon = (type: NavigationContext['type']): string => {
    switch (type) {
      case 'fixed': return '📌';
      case 'user': return '👤';
      case 'dynamic': return '🔄';
      default: return '📋';
    }
  };

  // Get group label for context type
  const getContextGroup = (type: NavigationContext['type']): string => {
    switch (type) {
      case 'fixed': return 'System Contexts';
      case 'user': return 'My Contexts';
      case 'dynamic': return 'FileMaker Contexts';
      default: return 'Other';
    }
  };

  return (
    <div className="context-selector">
      <div className="context-selector-header">
        <h3>Navigation Contexts</h3>
        {allowCreate && (
          <button 
            className="btn-create-context"
            onClick={onContextCreate}
          >
            ➕ New Context
          </button>
        )}
      </div>

      <div className="context-selector-list">
        <Selectr
          source="array"
          items={contextItems}
          mode="single"
          uiMode="list"
          searchable={true}
          grouped={true}
          placeholder="Search contexts..."
          onSelect={handleContextSelect}
          closeOnSelect={false}
          width="100%"
        />
      </div>

      {selectedContext && mode === 'view' && (
        <ContextViewer
          context={selectedContext}
          onEdit={allowEdit ? () => onContextEdit?.(selectedContext) : undefined}
          onDelete={allowDelete && selectedContext.type === 'user' 
            ? () => onContextDelete?.(selectedContext.id) 
            : undefined
          }
        />
      )}

      {selectedContext && mode === 'edit' && (
        <ContextEditor
          context={selectedContext}
          onSave={(updated) => {
            setSelectedContext(updated);
            onContextChange?.(updated);
          }}
        />
      )}
    </div>
  );
};

/**
 * ContextViewer - Display context details
 */
interface ContextViewerProps {
  context: NavigationContext;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ContextViewer: React.FC<ContextViewerProps> = ({ context, onEdit, onDelete }) => {
  return (
    <div className="context-viewer">
      <div className="context-header">
        <h4>{context.icon} {context.name}</h4>
        <div className="context-actions">
          {onEdit && (
            <button onClick={onEdit} className="btn-edit">
              ✏️ Edit
            </button>
          )}
          {onDelete && (
            <button onClick={onDelete} className="btn-delete">
              🗑️ Delete
            </button>
          )}
        </div>
      </div>

      {context.description && (
        <p className="context-description">{context.description}</p>
      )}

      <div className="context-info">
        <span className="info-label">Type:</span>
        <span className="info-value">{context.type}</span>
      </div>

      <div className="context-info">
        <span className="info-label">Items:</span>
        <span className="info-value">{context.items.length}</span>
      </div>

      <div className="context-items">
        <h5>Navigation Items:</h5>
        <ul>
          {context.items.map(item => (
            <li key={item.id}>
              {item.icon} {item.label}
              {item.description && (
                <span className="item-description">{item.description}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/**
 * ContextEditor - Edit context configuration
 */
interface ContextEditorProps {
  context: NavigationContext;
  onSave: (context: NavigationContext) => void;
}

const ContextEditor: React.FC<ContextEditorProps> = ({ context, onSave }) => {
  const [editedContext, setEditedContext] = useState<NavigationContext>(context);

  const handleSave = () => {
    onSave(editedContext);
  };

  return (
    <div className="context-editor">
      <h4>Edit Context</h4>
      
      <div className="editor-field">
        <label>Name:</label>
        <input
          type="text"
          value={editedContext.name}
          onChange={(e) => setEditedContext({ ...editedContext, name: e.target.value })}
        />
      </div>

      <div className="editor-field">
        <label>Description:</label>
        <textarea
          value={editedContext.description || ''}
          onChange={(e) => setEditedContext({ ...editedContext, description: e.target.value })}
        />
      </div>

      <div className="editor-field">
        <label>Icon:</label>
        <input
          type="text"
          value={editedContext.icon || ''}
          onChange={(e) => setEditedContext({ ...editedContext, icon: e.target.value })}
          placeholder="📋"
        />
      </div>

      <div className="editor-actions">
        <button onClick={handleSave} className="btn-save">
          💾 Save
        </button>
      </div>
    </div>
  );
};

export default ContextSelector;
